import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { CreditCard } from '../types';
import { formatCurrency, calculatePaymentDueDate } from '../utils/calculations';
import { startOfDay } from 'date-fns';

interface CreditCardManagerProps {
  showAddForm: boolean;
  setShowAddForm: (show: boolean) => void;
}

const CreditCardManager: React.FC<CreditCardManagerProps> = ({ showAddForm, setShowAddForm }) => {
  const { state, dispatch } = useAppContext();

  // Helper function to get next payment date for display
  const getNextPaymentDate = (card: CreditCard): Date => {
    const today = startOfDay(new Date());
    return calculatePaymentDueDate(card.paymentDayOfMonth, today);
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    currentBalance: string;
    paymentDayOfMonth: string;
  }>({
    name: '',
    currentBalance: '',
    paymentDayOfMonth: ''
  });

  const startEdit = (card: CreditCard) => {
    setEditingId(card.id);
    setEditForm({
      name: card.name,
      currentBalance: card.currentBalance.toString(),
      paymentDayOfMonth: card.paymentDayOfMonth.toString()
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      name: '',
      currentBalance: '',
      paymentDayOfMonth: ''
    });
  };

  const saveEdit = () => {
    if (!editingId) return;

    const paymentDay = editForm.paymentDayOfMonth === 'LDOM' ? 'LDOM' : parseInt(editForm.paymentDayOfMonth);
    const balance = parseFloat(editForm.currentBalance);

    if (isNaN(balance) || (paymentDay !== 'LDOM' && (isNaN(paymentDay as number) || (paymentDay as number) < 1 || (paymentDay as number) > 31))) {
      alert('Please enter valid values');
      return;
    }

    dispatch({
      type: 'UPDATE_CREDIT_CARD',
      payload: {
        id: editingId,
        creditCard: {
          name: editForm.name,
          currentBalance: balance,
          paymentDayOfMonth: paymentDay
        }
      }
    });

    cancelEdit();
  };

  const deleteCard = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete the "${name}" credit card?`)) {
      dispatch({ type: 'DELETE_CREDIT_CARD', payload: id });
    }
  };

  const [newCard, setNewCard] = useState({
    name: '',
    currentBalance: '0',
    paymentDayOfMonth: '1'
  });

  const saveNew = () => {
    const paymentDay = newCard.paymentDayOfMonth === 'LDOM' ? 'LDOM' : parseInt(newCard.paymentDayOfMonth);
    const balance = parseFloat(newCard.currentBalance);

    if (!newCard.name || isNaN(balance) || (paymentDay !== 'LDOM' && (isNaN(paymentDay as number) || (paymentDay as number) < 1 || (paymentDay as number) > 31))) {
      alert('Please enter valid values');
      return;
    }

    dispatch({
      type: 'ADD_CREDIT_CARD',
      payload: {
        name: newCard.name,
        currentBalance: balance,
        paymentDayOfMonth: paymentDay,
        isActive: true
      }
    });

    setNewCard({
      name: '',
      currentBalance: '0',
      paymentDayOfMonth: '1'
    });
    setShowAddForm(false);
  };

  const cancelNew = () => {
    setNewCard({
      name: '',
      currentBalance: '0',
      paymentDayOfMonth: '1'
    });
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
        <p className="text-sm">
          &emsp; Set the bill amount you need to pay. After each payment date,
          the balance resets to £0. Update the amount each month for your new bill.
        </p>
      </div>

      {state.creditCards.length === 0 && !showAddForm ? (
        <div className="text-center text-gray-500 py-8">
          No credit cards configured. Add your first credit card to get started!
        </div>
      ) : (
        <div className="table-container payments-table">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Card Name</th>
                <th className="text-right">Bill Amount</th>
                <th className="text-center">Payment Day</th>
                <th className="text-center">Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.creditCards.map(card => (
                <tr key={card.id}>
                  {editingId === card.id ? (
                    <>
                      <td className="text-left">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Card name"
                        />
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.currentBalance}
                          onChange={(e) => setEditForm({ ...editForm, currentBalance: e.target.value })}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-right"
                        />
                      </td>
                      <td className="text-center">
                        <select
                          value={editForm.paymentDayOfMonth}
                          onChange={(e) => setEditForm({ ...editForm, paymentDayOfMonth: e.target.value })}
                          className="px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                          ))}
                          <option value="LDOM">Last Day</option>
                        </select>
                      </td>
                      <td className="text-center">-</td>
                      <td className="text-center">
                        <div className="flex space-x-1 justify-center">
                          <button
                            onClick={saveEdit}
                            className="btn-secondary text-xs px-2 py-1"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="btn-danger text-xs px-2 py-1"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-left font-medium text-gray-800">{card.name}</td>
                      <td className="text-right font-bold text-red-600">
                        {formatCurrency(card.currentBalance)}
                      </td>
                      <td className="text-center text-gray-600">
                        {card.paymentDayOfMonth === 'LDOM' ? 'Last Day' : `${card.paymentDayOfMonth}`}
                      </td>
                      <td className="text-center">
                        <span className={`pill ${card.isActive ? 'pill-success' : 'pill-warning'}`}>
                          {card.isActive ? '✓ Active' : '⏸ Inactive'}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="flex space-x-1 justify-center">
                          <button
                            onClick={() => startEdit(card)}
                            className="btn-secondary text-xs px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCard(card.id, card.name)}
                            className="btn-danger text-xs px-2 py-1"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {showAddForm && (
                <tr>
                  <td className="text-left">
                    <input
                      type="text"
                      value={newCard.name}
                      onChange={(e) => setNewCard({ ...newCard, name: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="Card name"
                    />
                  </td>
                  <td className="text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={newCard.currentBalance}
                      onChange={(e) => setNewCard({ ...newCard, currentBalance: e.target.value })}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-right"
                    />
                  </td>
                  <td className="text-center">
                    <select
                      value={newCard.paymentDayOfMonth}
                      onChange={(e) => setNewCard({ ...newCard, paymentDayOfMonth: e.target.value })}
                      className="px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                      <option value="LDOM">Last Day</option>
                    </select>
                  </td>
                  <td className="text-center">
                    <span className="pill pill-success">✓ Active</span>
                  </td>
                  <td className="text-center">
                    <div className="flex space-x-1 justify-center">
                      <button
                        onClick={saveNew}
                        className="btn-secondary text-xs px-2 py-1"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelNew}
                        className="btn-danger text-xs px-2 py-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CreditCardManager;